import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus
} from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const STATUS_COLORS = {
  'Da definire': '#0284c7',
  'In corso': '#2563eb',
  Definita: '#16a34a',
  Archiviata: '#64748b',
  Sospesa: '#d97706'
};

const DATE_EVENT_CONFIG = [
  { key: 'data_udienza', label: 'Data udienza' },
  { key: 'data_scadenza', label: 'Scadenza' },
  { key: 'termine_costituzione', label: 'Costituzione' },
  { key: 'termine_memorie', label: 'Memorie' },
  { key: 'data_firma', label: 'Firma' },
  { key: 'data_apertura', label: 'Apertura' },
  { key: 'data_chiusura', label: 'Chiusura' }
];

const toIsoDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const dateOnly = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return dateOnly;
    }
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatEventType = (type) => {
  const found = DATE_EVENT_CONFIG.find((item) => item.key === type);
  return found?.label || type || '-';
};

const parseIsoDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return null;
  return new Date(`${iso}T00:00:00`);
};

const formatDate = (value) => {
  const iso = toIsoDate(value);
  if (!iso) return '-';
  return new Date(`${iso}T00:00:00`).toLocaleDateString('it-IT');
};

const getDaysUntilLabel = (dateValue) => {
  const target = parseIsoDate(dateValue);
  if (!target) return '-';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Scaduta';
  if (diffDays === 0) return 'Oggi';
  if (diffDays === 1) return 'Domani';
  return `In ${diffDays} giorni`;
};

export default function CalendarioPraticheUO() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateTypeFilter, setDateTypeFilter] = useState('all');
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [calendarTitle, setCalendarTitle] = useState('');

  const fetchAllPratiche = useCallback(async () => {
    setLoading(true);
    try {
      const collected = [];
      const limit = 200;
      let page = 1;
      let total = 0;

      do {
        const res = await api.get('/pratiche-urgenze-opposizioni', {
          params: {
            page,
            limit,
            orderBy: 'data_scadenza',
            order: 'asc'
          }
        });
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        total = Number(res.data?.total || 0);
        collected.push(...data);
        page += 1;
      } while (collected.length < total);

      setRows(collected);
    } catch (error) {
      console.error('Error loading pratiche U/O calendar data:', error);
      toast.error('Errore nel caricamento del calendario pratiche U/O');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllPratiche();
  }, [fetchAllPratiche]);

  const statusOptions = useMemo(() => {
    const set = new Set(rows.map((row) => row.stato_pratica).filter(Boolean));
    return Array.from(set);
  }, [rows]);

  const events = useMemo(() => {
    const calendarEvents = [];

    rows.forEach((row) => {
      DATE_EVENT_CONFIG.forEach((dateField) => {
        const eventDate = toIsoDate(row[dateField.key]);
        if (!eventDate) return;

        const statusColor = STATUS_COLORS[row.stato_pratica] || '#6b7280';
        calendarEvents.push({
          id: `${row.id}-${dateField.key}`,
          title: `${row.cliente_nome || row.codice_pratica || `Pratica #${row.id}`}`,
          start: eventDate,
          allDay: true,
          backgroundColor: '#ffffff',
          borderColor: '#e2e8f0',
          textColor: '#0f172a',
          extendedProps: {
            praticaId: row.id,
            codicePratica: row.codice_pratica,
            clienteNome: row.cliente_nome,
            statusColor,
            statoPratica: row.stato_pratica,
            tipoAtto: row.tipo_atto_nome,
            tipoAzione: row.tipo_pratica_azione_nome,
            tribunale: row.tribunale_nome,
            oggetto: row.oggetto,
            dateType: dateField.key,
            dateValue: eventDate
          }
        });
      });
    });

    return calendarEvents.filter((event) => {
      if (statusFilter !== 'all' && event.extendedProps.statoPratica !== statusFilter) {
        return false;
      }
      if (dateTypeFilter !== 'all' && event.extendedProps.dateType !== dateTypeFilter) {
        return false;
      }
      return true;
    });
  }, [rows, statusFilter, dateTypeFilter]);

  const upcomingEvents = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [...events]
      .filter((event) => {
        const d = parseIsoDate(event.start);
        return d && d >= today;
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      .slice(0, 8);
  }, [events]);

  const changeView = (viewType) => {
    const apiRef = calendarRef.current?.getApi();
    if (!apiRef) return;
    apiRef.changeView(viewType);
  };

  const goPrev = () => {
    const apiRef = calendarRef.current?.getApi();
    if (!apiRef) return;
    apiRef.prev();
  };

  const goNext = () => {
    const apiRef = calendarRef.current?.getApi();
    if (!apiRef) return;
    apiRef.next();
  };

  const goToday = () => {
    const apiRef = calendarRef.current?.getApi();
    if (!apiRef) return;
    apiRef.today();
  };

  const handleEventClick = (info) => {
    const praticaId = info?.event?.extendedProps?.praticaId;
    if (praticaId) {
      navigate(`/pratiche-urgenze-opposizioni?editId=${praticaId}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: '#e0f2fe',
              color: '#0284c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CalendarDays size={18} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Calendario U/O
          </Typography>
          <Box />
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => navigate('/pratiche-urgenze-opposizioni')}
          sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#020617' } }}
        >
          Nuovo evento
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Visualizza e gestisci udienze e scadenze delle pratiche urgenze/opposizioni
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' },
          gap: 2,
          alignItems: { xs: 'start', lg: 'stretch' }
        }}
      >
        <StyledCard>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.2)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.25,
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" onClick={goToday}>
                Oggi
              </Button>
              <IconButton size="small" onClick={goPrev}>
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton size="small" onClick={goNext}>
                <ChevronRight size={16} />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700, ml: 1, textTransform: 'capitalize' }}>
                {calendarTitle}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant={currentView === 'dayGridMonth' ? 'contained' : 'outlined'}
                onClick={() => changeView('dayGridMonth')}
                sx={currentView === 'dayGridMonth' ? { bgcolor: '#0f172a', '&:hover': { bgcolor: '#020617' } } : undefined}
              >
                Mese
              </Button>
              <Button
                size="small"
                variant={currentView === 'timeGridWeek' ? 'contained' : 'outlined'}
                onClick={() => changeView('timeGridWeek')}
                sx={currentView === 'timeGridWeek' ? { bgcolor: '#0f172a', '&:hover': { bgcolor: '#020617' } } : undefined}
              >
                Settimana
              </Button>
              <Button
                size="small"
                variant={currentView === 'timeGridDay' ? 'contained' : 'outlined'}
                onClick={() => changeView('timeGridDay')}
                sx={currentView === 'timeGridDay' ? { bgcolor: '#0f172a', '&:hover': { bgcolor: '#020617' } } : undefined}
              >
                Giorno
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.16)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Stato pratica</InputLabel>
              <Select
                label="Stato pratica"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Tipo data</InputLabel>
              <Select
                label="Tipo data"
                value={dateTypeFilter}
                onChange={(e) => setDateTypeFilter(e.target.value)}
              >
                <MenuItem value="all">Tutte</MenuItem>
                {DATE_EVENT_CONFIG.map((item) => (
                  <MenuItem key={item.key} value={item.key}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${events.length} eventi`}
              sx={{ alignSelf: 'center' }}
            />
          </Box>

          <Box sx={{ p: 2 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  '.fc-theme-standard td, .fc-theme-standard th': {
                    borderColor: '#e2e8f0'
                  },
                  '.fc .fc-scrollgrid': {
                    borderColor: '#e2e8f0',
                    borderRadius: 2
                  },
                  '.fc .fc-col-header-cell': {
                    backgroundColor: '#f8fafc'
                  },
                  '.fc .fc-col-header-cell-cushion': {
                    color: '#0f172a',
                    fontWeight: 700,
                    textDecoration: 'none',
                    padding: '8px 4px'
                  },
                  '.fc .fc-daygrid-day.fc-day-today': {
                    backgroundColor: '#e0f2fe'
                  },
                  '.fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-frame': {
                    backgroundColor: '#e0f2fe',
                    border: 'none',
                    borderRadius: 0,
                    boxShadow: 'none'
                  },
                  '.fc .fc-daygrid-day-number': {
                    color: '#0f172a',
                    fontWeight: 700
                  },
                  '.fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number': {
                    color: '#0b4a6f',
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    minWidth: 'auto',
                    height: 'auto',
                    display: 'inline-block',
                    margin: '4px 4px 0 0',
                    fontWeight: 800
                  },
                  '.fc .fc-daygrid-day-events': {
                    marginTop: 2
                  },
                  '.fc .fc-daygrid-event': {
                    marginTop: 2
                  },
                  '.fc .fc-event': {
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    padding: 0,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                    overflow: 'hidden'
                  },
                  '.fc .fc-daygrid-more-link': {
                    fontWeight: 700,
                    color: '#2563eb'
                  },
                  '.fc .fc-daygrid-event-dot': {
                    display: 'none'
                  }
                }}
              >
                <FullCalendar
                  ref={calendarRef}
                  locale={itLocale}
                  plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={false}
                  buttonText={{
                    today: 'Oggi',
                    month: 'Mese',
                    week: 'Settimana',
                    day: 'Giorno'
                  }}
                  events={events}
                  eventClick={handleEventClick}
                  eventContent={(arg) => {
                    const props = arg.event.extendedProps || {};
                    const title = props.clienteNome || props.codicePratica || arg.event.title;
                    const tipoAtto = props.tipoAtto || '-';
                    const tipoAzione = props.tipoAzione || '-';
                    const accentColor = props.statusColor || '#2563eb';
                    const isMonthView = currentView === 'dayGridMonth';
                    return (
                      <Box
                        sx={{
                          width: '100%',
                          backgroundColor: '#ffffff',
                          borderLeft: `3px solid ${accentColor}`,
                          px: isMonthView ? 0.5 : 0.75,
                          py: isMonthView ? 0.35 : 0.5
                        }}
                      >
                        {!isMonthView && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: accentColor,
                              fontWeight: 700,
                              display: 'block',
                              fontSize: '0.66rem',
                              lineHeight: 1.15,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {formatEventType(props.dateType)}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 700,
                            color: '#0f172a',
                            fontSize: '0.72rem',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {title}
                        </Typography>
                        {!isMonthView && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', color: '#0f172a', fontWeight: 600, lineHeight: 1.2 }}
                          >
                            {tipoAtto}
                          </Typography>
                        )}
                        {!isMonthView && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', lineHeight: 1.2 }}>
                            {tipoAzione}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            color: '#475569',
                            fontSize: isMonthView ? '0.66rem' : '0.72rem',
                            lineHeight: 1.15,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {props.tribunale || '-'}
                        </Typography>
                      </Box>
                    );
                  }}
                  height={currentView === 'dayGridMonth' ? 560 : 'auto'}
                  aspectRatio={currentView === 'dayGridMonth' ? 3.1 : 1.35}
                  expandRows={false}
                  firstDay={1}
                  navLinks
                  dayMaxEvents={currentView === 'dayGridMonth' ? 2 : true}
                  datesSet={(info) => {
                    setCurrentView(info.view.type);
                    setCalendarTitle(info.view.title);
                  }}
                />
              </Box>
            )}
          </Box>
        </StyledCard>

        <Card
          variant="outlined"
          sx={{
            p: 2,
            height: { xs: 'auto', lg: '100%' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Prossimi eventi
            </Typography>
            <Button size="small" onClick={fetchAllPratiche} disabled={loading}>
              Aggiorna
            </Button>
          </Box>
          <Box sx={{ display: 'grid', gap: 1, overflowY: 'auto', pr: 0.5, minHeight: 0 }}>
            {upcomingEvents.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nessun evento imminente con i filtri attuali.
              </Typography>
            )}
            {upcomingEvents.map((event) => {
              const props = event.extendedProps || {};
              const accentColor = props.statusColor || '#2563eb';
              const title = props.clienteNome || props.codicePratica || event.title;
              return (
                <Card
                  key={`upcoming-${event.id}`}
                  variant="outlined"
                  onClick={() => navigate(`/pratiche-urgenze-opposizioni?editId=${props.praticaId}`)}
                  sx={{ p: 1.25, cursor: 'pointer', borderLeft: `3px solid ${accentColor}` }}
                >
                  <Typography variant="caption" sx={{ color: accentColor, fontWeight: 700 }}>
                    {formatDate(event.start)} - {formatEventType(props.dateType)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {title}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#0f172a', fontWeight: 600 }}>
                    {props.tipoAtto || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                    {props.tipoAzione || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {props.tribunale || '-'}
                  </Typography>
                  <Chip size="small" color="success" label={getDaysUntilLabel(event.start)} sx={{ mt: 0.75 }} />
                </Card>
              );
            })}
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
